import { isField } from "@formily/core";

import { buildApiUrl, fetchAPI } from "@neu/libui";
import type { FormValidatorFn } from "@neu/libui";

/**
 * Tier 3 Validator: Validate patient dates
 * If discharge_date exists and is before admission_date, set field error and block submission
 */
export const validatePatientDates: FormValidatorFn = ({ form, values }) => {
	const { discharge_date, admission_date } = values;

	// Only validate if both dates exist
	if (!discharge_date || !admission_date) {
		return { passed: true };
	}

	const dischargeDate = new Date(discharge_date as string | number | Date);
	const admissionDate = new Date(admission_date as string | number | Date);

	// Check if dates are valid
	if (
		Number.isNaN(dischargeDate.getTime()) ||
		Number.isNaN(admissionDate.getTime())
	) {
		return { passed: true };
	}

	// Clear any previously-set Tier 3 feedback on this field so
	// Formily doesn't block re-submission when the issue is fixed.
	const dischargeDateField = form.query("discharge_date").take();
	if (dischargeDateField && isField(dischargeDateField)) {
		dischargeDateField.setFeedback({
			type: "error",
			code: "FormValidatorError",
			messages: [],
		});
	}

	// If discharge_date is before admission_date, set error and block submission
	if (dischargeDate < admissionDate) {
		if (dischargeDateField && isField(dischargeDateField)) {
			dischargeDateField.setFeedback({
				type: "error",
				code: "FormValidatorError",
				messages: ["Discharge date cannot be before admission date"],
			});
		}
		return {
			passed: false,
			message: "Discharge date cannot be before admission date",
		};
	}

	return { passed: true };
};

/**
 * Tier 3 Validator: Validate required insurance
 * If patient status is "On-Service", ensure insurance information is provided
 */
export const validateRequiredInsurance: FormValidatorFn = ({
	form,
	values,
}) => {
	const {
		status,
		patient_insurance_bin,
		patient_insurance_pcn,
		patient_insurance_group_no,
	} = values;

	// Only validate if status is "On-Service"
	if (status !== "On-Service") {
		return { passed: true };
	}

	// Check if at least one insurance field is filled
	const hasInsurance =
		patient_insurance_bin ||
		patient_insurance_pcn ||
		patient_insurance_group_no;

	// Clear any previously-set Tier 3 feedback so Formily doesn't
	// block re-submission when the user has fixed the issue.
	const insuranceField = form.query("patient_insurance_bin").take();
	if (insuranceField && isField(insuranceField)) {
		insuranceField.setFeedback({
			type: "error",
			code: "FormValidatorError",
			messages: [],
		});
	}

	if (!hasInsurance) {
		// Set error on the first insurance field
		if (insuranceField && isField(insuranceField)) {
			insuranceField.setFeedback({
				type: "error",
				code: "FormValidatorError",
				messages: [
					"At least one insurance field is required for On-Service patients",
				],
			});
		}
		return {
			passed: false,
			message:
				"Insurance information is required for On-Service patients",
		};
	}

	return { passed: true };
};

/**
 * Tier 3 Validator: Prevent duplicate member+plan create/update collisions.
 * Shows an inline field error instead of surfacing a generic HTTP 403.
 */
export const validateDuplicateMemberPlan: FormValidatorFn = async ({
	form,
	values,
}) => {
	const externalID = String(values.external_id ?? "").trim();
	const planID = Number(values.plan_id?.id ?? values.plan_id);
	if (!externalID || !planID) {
		return { passed: true };
	}

	const externalIDField = form.query("external_id").take();
	if (externalIDField && isField(externalIDField)) {
		externalIDField.setFeedback({
			type: "error",
			code: "FormValidatorError",
			messages: [],
		});
	}

	try {
		const response = await fetchAPI(
			buildApiUrl(
				`/patient/list?limit=2&_external_id=eq:${encodeURIComponent(
					externalID
				)}&_plan_id=eq:${planID}`
			)
		);
		const rows = Array.isArray(response)
			? response
			: (response?.data ?? []);
		const currentID = values.id ? String(values.id) : null;
		const conflicting = rows.find(
			(row: Record<string, unknown>) => String(row.id ?? "") !== currentID
		);

		if (conflicting) {
			const message =
				"A patient with this Member ID already exists for the selected plan.";
			if (externalIDField && isField(externalIDField)) {
				externalIDField.setFeedback({
					type: "error",
					code: "FormValidatorError",
					messages: [message],
				});
			}
			return { passed: false, message };
		}
	} catch {
		// API failure should not block submission.
	}

	return { passed: true };
};
